import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  Observable,
  distinctUntilChanged,
  finalize,
  map,
  startWith,
  takeUntil,
} from 'rxjs';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { RESTAPIRequestResponseDetailsResponse } from '../../models/rest-api-request-response-details.model';
import { RestApiRequestResponseDetailsService } from '../../services/rest-api-request-response-details.service';
import { ClientSelectionService } from '../clients/client-selection.service';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { toggleAutocompletePanel } from '../../shared/utils/autocomplete-panel.utils';

class DateRangeErrorStateMatcher implements ErrorStateMatcher {
  constructor(private readonly hasDateRangeMismatch: () => boolean) {}

  isErrorState(): boolean {
    return this.hasDateRangeMismatch();
  }
}

@Component({
  selector: 'app-rest-api-request-response-details',
  standalone: false,
  templateUrl: './rest-api-request-response-details.component.html',
  styleUrl: './rest-api-request-response-details.component.css',
})
export class RestApiRequestResponseDetailsComponent extends BaseTableComponent<RESTAPIRequestResponseDetailsResponse> {
  override sortBy = 'requestedDate';
  override sortDir: 'asc' | 'desc' = 'desc';
  private hasAppliedFilter = false;
  private selectedClientName = '';
  readonly dateRangeErrorStateMatcher = new DateRangeErrorStateMatcher(() =>
    this.hasDateRangeMismatch(),
  );

  readonly apiNameMap: Readonly<Record<string, string>> = {
    S111InputOutputInternalServiceAPI: 's111-input-output-service',
  };

  readonly apiNameKeys: string[] = Object.keys(this.apiNameMap);

  readonly apiMethods: string[] = [
    'GetClaimStatus',
    'GetBeneficiaryEnrollment',
    'SaveClaim',
    'DeleteClaim',
  ].sort((a, b) => a.localeCompare(b));

  readonly statuses: string[] = ['Error', 'Failed', 'Success'];

  apiNameControl = new FormControl<string>('', { nonNullable: true });
  filteredApiName$: Observable<string[]> =
    this.apiNameControl.valueChanges.pipe(
      startWith(this.apiNameControl.value),
      map((value) => this.filterOptions(this.apiNameKeys, value)),
    );

  apiMethodControl = new FormControl<string>('', { nonNullable: true });
  filteredApiMethods$: Observable<string[]> =
    this.apiMethodControl.valueChanges.pipe(
      startWith(this.apiMethodControl.value),
      map((value) => this.filterOptions(this.apiMethods, value)),
    );

  statusControl = new FormControl<string>('', { nonNullable: true });

  filteredStatuses$: Observable<string[]> =
    this.statusControl.valueChanges.pipe(
      startWith(this.statusControl.value),
      map((value) => this.filterOptions(this.statuses, value)),
    );

  readonly displayedColumns: string[] = [
    'apiName',
    'apiMethod',
    'claimKey',
    'requestedBy',
    'requestedDate',
    'responseDate',
    's3RequestFileName',
    's3ResponseFileName',
    'status',
  ];

  readonly filter = {
    apiName: '',
    apiMethod: '',
    claimKey: '',
    requestedBy: '',
    fromRequestedDate: null as Date | null,
    toRequestedDate: null as Date | null,
    status: '',
  };

  constructor(
    private restApiRequestResponseDetailsService: RestApiRequestResponseDetailsService,
    private clientSelection: ClientSelectionService,
  ) {
    super();
  }

  onFilter(): void {
    const selectedApiNameKey = this.normalizeSelection(
      this.apiNameKeys,
      this.apiNameControl.value,
    );
    this.filter.apiName = selectedApiNameKey
      ? this.apiNameMap[selectedApiNameKey]
      : '';
    this.apiNameControl.setValue(selectedApiNameKey, { emitEvent: false });

    const selectedApiMethod = this.normalizeSelection(
      this.apiMethods,
      this.apiMethodControl.value,
    );
    this.filter.apiMethod = selectedApiMethod;
    this.apiMethodControl.setValue(selectedApiMethod, { emitEvent: false });

    const selectedStatus = this.normalizeSelection(
      this.statuses,
      this.statusControl.value,
    );
    this.filter.status = selectedStatus;
    this.statusControl.setValue(selectedStatus, { emitEvent: false });

    this.resolveClientName();
    if (!this.hasValidRequestedDateRange()) {
      this.errorMessage =
        'Please provide both Requested Date From and Requested Date To.';
      return;
    }

    if (!this.selectedClientName.trim()) {
      this.errorMessage =
        'Please select a client to view REST API request/response details.';
      this.dataSource.data = [];
      this.totalCount = 0;
      this.hasAppliedFilter = false;
      return;
    }

    this.hasAppliedFilter = true;
    this.pageIndex = 0;
    this.loadDataWrapper();
  }

  clearSelectedApiName(event: Event): void {
    event.stopPropagation();
    this.apiNameControl.setValue('');
    this.filter.apiName = '';
  }

  clearSelectedApiMethod(event: Event): void {
    event.stopPropagation();
    this.apiMethodControl.setValue('');
    this.filter.apiMethod = '';
  }

  clearSelectedStatus(event: Event): void {
    event.stopPropagation();
    this.statusControl.setValue('');
    this.filter.status = '';
  }

  openOrClosePanel(event: Event, trigger: MatAutocompleteTrigger): void {
    toggleAutocompletePanel(event, trigger);
  }

  displayOption(value: string | null): string {
    return value ?? '';
  }

  protected override loadData(skip: number, count: number): void {
    if (!this.hasAppliedFilter) {
      this.dataSource.data = [];
      this.totalCount = 0;
      this.errorMessage = '';
      return;
    }

    if (!this.hasValidRequestedDateRange()) {
      this.errorMessage =
        'Please provide both Requested Date From and Requested Date To.';
      return;
    }

    this.loading = true;

    this.restApiRequestResponseDetailsService
      .getRestAPIRequestResponseDetails(
        this.selectedClientName,
        this.filter.apiName,
        this.filter.apiMethod,
        this.filter.claimKey,
        this.filter.requestedBy,
        this.toApiDate(this.filter.fromRequestedDate),
        this.toApiDate(this.filter.toRequestedDate),
        this.filter.status,
        this.sortBy,
        this.sortDir,
        skip,
        count,
      )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.logs ?? [];
          this.totalCount = response.totalCount ?? 0;
          this.errorMessage = '';
        },
        error: (err) => {
          this.errorMessage = formatHttpErrorMessage(
            'Failed to load REST API request/response details',
            err,
          );
          this.dataSource.data = [];
          this.totalCount = 0;
        },
      });
  }

  private resolveClientName(): void {
    this.clientSelection.selectedClient$
      .pipe(
        map((client) => client?.DBName ?? ''),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((dbName) => {
        this.selectedClientName = dbName;

        this.pageIndex = 0;
        if (this.selectedClientName.trim()) {
          this.hasAppliedFilter = true;
          this.errorMessage = '';
          this.loadDataWrapper();
        } else {
          this.hasAppliedFilter = false;
          this.dataSource.data = [];
          this.totalCount = 0;
          this.errorMessage =
            'Please select a client to view REST API request/response details.';
        }
      });
  }

  private toApiDate(value: Date | null): string {
    if (!value) {
      return '';
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private filterOptions(options: string[], value: string): string[] {
    const normalizedValue = value.trim().toLowerCase();
    return options.filter((option) =>
      option.toLowerCase().includes(normalizedValue),
    );
  }

  private normalizeSelection(options: string[], value: string): string {
    const normalizedValue = value.trim().toLowerCase();
    const matched = options.find(
      (option) => option.toLowerCase() === normalizedValue,
    );
    return matched ?? '';
  }

  private hasValidRequestedDateRange(): boolean {
    return !this.hasDateRangeMismatch();
  }

  isRequestedDateRangeMismatch(): boolean {
    return this.hasDateRangeMismatch();
  }

  private hasDateRangeMismatch(): boolean {
    const hasFrom = this.filter.fromRequestedDate !== null;
    const hasTo = this.filter.toRequestedDate !== null;
    return hasFrom !== hasTo;
  }
}
